import { AfterContentInit, Component, OnDestroy } from '@angular/core';
import { DialogBase, DialogCloseReason } from '../../dialogs/base-dialog';
import { AndCondition } from '../models/composite-conditions.model';
import { Condition } from '../models/condition.model';
import { ConditionDialogService } from './condition-dialog.service';
import { CrmEntitiesService } from '../../crm-entities/crm-entities.service';
import { DialogService } from '../../dialogs/dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'conditions-dialog',
  templateUrl: './conditions-dialog.html',
  providers: [ConditionDialogService]
})
export class ConditionsDialogComponent extends DialogBase implements AfterContentInit, OnDestroy {
  conditionsRootModel: AndCondition; // the root is always an implicit 'And' composite condition
  readonly MaxConditionTreeHeight = 3;
  formInvalid: boolean = false;
  showRequiredErrorMessage: boolean = false;
  showDatesBetweenErrorMessage: boolean = false;
  showEntityAttributeErrorMessage: boolean = false;
  showNumberMaxErrorMessage: boolean = false;
  showDecimalMaxErrorMessage: boolean = false;
  showValidationErrors: boolean = false;
  checkValidityEventSubscription: Subscription;

  constructor(public conditionDialogService: ConditionDialogService,
              private dialogService: DialogService,
              private translate: TranslateService,
              public crmEntitiesService: CrmEntitiesService) {
    super();

    this.conditionDialogService.conditionsForm.valueChanges.subscribe(form => {
      this.conditionDialogService.agregateFormValidationErrors();

      setTimeout(() => {
        this.getFormValidationFlags();
      }, 0);
    });

    this.checkValidityEventSubscription = this.conditionDialogService.doCheckValidity.subscribe(() => {
      this.conditionDialogService.markAllControlsAsTouched();
      this.getFormValidationFlags();

      if (this.formInvalid) {
        this.showValidationErrors = true;
      } else {
        this.showValidationErrors = false;
      }
    });
  }

  ngAfterContentInit(): void {
    this.showEntityAttributeErrorMessage = this.conditionDialogService.showEntityAttributeErrorMessage;
  }

  getFormValidationFlags() {
    this.formInvalid = this.conditionDialogService.conditionsForm ?
      (this.conditionDialogService.conditionsForm.invalid ? true : false) : false;
    this.showRequiredErrorMessage = this.conditionDialogService.showRequiredErrorMessage;
    this.showDatesBetweenErrorMessage = this.conditionDialogService.showDatesBetweenErrorMessage;
    this.showEntityAttributeErrorMessage = this.conditionDialogService.showEntityAttributeErrorMessage;
    this.showNumberMaxErrorMessage = this.conditionDialogService.showNumberMaxErrorMessage;
    this.showDecimalMaxErrorMessage = this.conditionDialogService.showDecimalMaxErrorMessage;
  }

  initDialog() {
    if (this.extraData && this.extraData.conditionsModel) {
      this.conditionsRootModel = this.extraData.conditionsModel;
    } else {
      // this is a new condition being added create an initial empty root
      this.conditionsRootModel = new AndCondition();
    }
  }

  onAddCondition() {
    const leafCondition = new Condition();
    this.conditionsRootModel.addChildCondition(leafCondition);
  }

  onDelete(event: any) {
    this.conditionDialogService.selectedConditions.forEach((selectedCondition) => {
      selectedCondition.parent.removeChildCondition(selectedCondition);
    });

    this.conditionDialogService.reduceConditionTreeAfterDelete(this.conditionsRootModel);

    this.conditionDialogService.clearSelection();
  }

  onGroupConditions(event: any, isOrGroup: boolean) {
    this.conditionDialogService.groupSelectedConditions(isOrGroup);

    if (this.showValidationErrors) {
      setTimeout(() => {
        this.conditionDialogService.markAllControlsAsTouched();
      }, 0);
    }
  }

  onUngroupConditions(event: any) {
    this.conditionDialogService.ungroupSelection();

    if (this.showValidationErrors) {
      setTimeout(() => {
        this.conditionDialogService.markAllControlsAsTouched();
      }, 0);
    }
  }

  shouldDisableGroupingButton() {
    const cds = this.conditionDialogService;
    return !(
      cds.allSelectedFromSameParent && (cds.selectedConditions.length > 1) &&
      (
        ( // this is the root
          cds.parentOfAllSelected &&
          !cds.parentOfAllSelected.parent
        )
        ||
        ( // this is not the root
          cds.parentOfAllSelected &&
          cds.parentOfAllSelected.parent &&
          cds.parentOfAllSelected.getConditionsCount() !== cds.selectedConditions.length
        )
      )
    );
  }

  get showErrors() {
    return (this.formInvalid && this.showValidationErrors);
  }

  onOk() {
    this.conditionDialogService.markAllControlsAsTouched();

    if (this.formInvalid) {
      this.showValidationErrors = true;
      return;
    }

    this.showValidationErrors = false;
    this.conditionDialogService.recreateFormValidationGroup();

    super.close({
      reason: DialogCloseReason.OK, data: {
        conditionModel: this.conditionsRootModel
      }
    });
  }

  ngOnDestroy(): void {
    this.checkValidityEventSubscription.unsubscribe();
  }
}
